import type {
  ArticleBlock,
  ArticleHeadingLevel,
  ArticleInline,
  ArticleListBlock,
  ArticleTableCell,
} from '~/content/learn';
import { resolvePayloadMedia } from '~/lib/payload-media';

/**
 * Convert a Payload Lexical `richText` value into the same `ArticleBlock[]` the
 * blocks editor produces.
 *
 * Deliberately targets the existing normalized shape rather than adding a second
 * renderer. `LearnArticleDetail` keeps rendering one union, so a rich-text article
 * and a block article emit byte-identical markup for equivalent content — same
 * heading levels, same quick-nav anchors, same wrappers. That is what makes this
 * migration SEO-neutral by construction instead of by inspection.
 *
 * Lexical node reference: https://payloadcms.com/docs/rich-text/overview
 */

/** Lexical serializes marks as a bitfield on text nodes. */
const FORMAT_BOLD = 1;
const FORMAT_ITALIC = 1 << 1;

interface LexicalNode {
  type?: string;
  tag?: string;
  format?: number | string;
  text?: string;
  listType?: string;
  fields?: Record<string, unknown> & { blockType?: string };
  children?: LexicalNode[];
  /** `link` nodes carry their destination here. */
  url?: string;
  newTab?: boolean;
  relationTo?: string;
  value?: unknown;
  headerState?: number;
  colSpan?: number;
  rowSpan?: number;
}

export interface LexicalRichText {
  root?: LexicalNode;
}

export function isLexicalRichText(value: unknown): value is LexicalRichText {
  return (
    typeof value === 'object' &&
    value !== null &&
    'root' in value &&
    typeof (value as LexicalRichText).root === 'object'
  );
}

/**
 * Article copy is CMS-authored, so a link target is untrusted input by the time it
 * reaches the renderer. Allowlist site-relative paths and the schemes that make
 * sense in prose; anything else — `javascript:` above all — yields no anchor at
 * all rather than a live one. Applied here and again at render time, because the
 * only reliable place to stop a scheme is immediately before it becomes an `href`.
 */
const SAFE_LINK_SCHEME = /^(?:https?:|mailto:|tel:)/i;
const INVALID_LINK_CHARACTERS = /[\\\u0000-\u001f\u007f]/;

export function safeArticleHref(href: string | undefined): string | undefined {
  const trimmed = href?.trim();
  if (!trimmed || INVALID_LINK_CHARACTERS.test(trimmed)) return undefined;
  // Path, query or fragment. `//host` is protocol-relative, not site-relative.
  if (/^[/#?]/.test(trimmed)) return trimmed.startsWith('//') ? undefined : trimmed;
  return SAFE_LINK_SCHEME.test(trimmed) ? trimmed : undefined;
}

/**
 * Resolve an internal reference link to a site path.
 *
 * Payload only populates the referenced document when the article is fetched at a
 * nonzero depth (see `getArticleByFullPath`). At depth 0 `value` is a bare id,
 * which carries no path — return nothing so the copy renders as plain text
 * instead of an anchor with no `href`.
 */
function referenceHref(doc: unknown): string | undefined {
  if (!doc || typeof doc !== 'object') return undefined;
  const value = (doc as { value?: unknown }).value;
  if (!value || typeof value !== 'object') return undefined;
  const fullPath = (value as { fullPath?: unknown }).fullPath;
  if (typeof fullPath !== 'string' || !fullPath) return undefined;
  return safeArticleHref(`/${fullPath.replace(/^\/+/, '')}`);
}

function linkTarget(node: LexicalNode): { href?: string; newTab?: boolean } {
  // Payload stores link config under `fields` for its own link node, and on the
  // node itself for the plain Lexical one. Support both.
  const fields = node.fields as { url?: string; newTab?: boolean; doc?: unknown } | undefined;
  const url = node.url ?? fields?.url ?? undefined;
  const resolved = safeArticleHref(url) ?? referenceHref(fields?.doc);
  if (!resolved) return {};
  return { href: resolved, ...(node.newTab || fields?.newTab ? { newTab: true } : {}) };
}

/** Flatten a node's descendants into inline runs, carrying marks and links down. */
function toInlineRuns(
  nodes: LexicalNode[] | undefined,
  inherited: Omit<ArticleInline, 'text'> = {},
): ArticleInline[] {
  if (!Array.isArray(nodes)) return [];

  return nodes.flatMap((node): ArticleInline[] => {
    if (node.type === 'linebreak') return [{ ...inherited, text: '\n' }];

    if (node.type === 'text') {
      const text = node.text ?? '';
      if (!text) return [];
      const format = typeof node.format === 'number' ? node.format : 0;
      return [
        {
          ...inherited,
          text,
          ...(format & FORMAT_BOLD ? { bold: true } : {}),
          ...(format & FORMAT_ITALIC ? { italic: true } : {}),
        },
      ];
    }

    if (node.type === 'link' || node.type === 'autolink') {
      return toInlineRuns(node.children, { ...inherited, ...linkTarget(node) });
    }

    // A sublist is handled by `listNodeToArticleBlock`. Descending into it here
    // would mash the child bullet into the parent's text.
    if (node.type === 'list' || node.type === 'listitem') return [];

    // Unknown inline wrapper — keep its text rather than dropping content.
    return toInlineRuns(node.children, inherited);
  });
}

/**
 * Convert a Lexical list without discarding its nested list boundaries.
 *
 * Nested lists remain attached to the parent item and retain their own ordered
 * flag. Flattening them into the outer list would turn ordered children into
 * bullets (or vice versa) whenever the two levels use different list types.
 */
function listNodeToArticleBlock(node: LexicalNode): ArticleListBlock | undefined {
  const entries = (node.children ?? [])
    .flatMap((item) => {
      const children = item.type === 'listitem' ? (item.children ?? []) : [];
      const runs = toInlineRuns(children);
      const nestedLists = children
        .filter((child) => child.type === 'list')
        .map(listNodeToArticleBlock)
        .filter((list): list is ArticleListBlock => Boolean(list));

      if (runsToText(runs).length === 0 && nestedLists.length === 0) return [];
      return [{ runs, nestedLists }];
    })
    .reduce<Array<{ nestedLists: ArticleListBlock[]; runs: ArticleInline[] }>>((kept, entry) => {
      const previous = kept.at(-1);
      // Lexical models a nested level as its own list item holding nothing but the
      // nested list, so rendering every item literally spends a bullet — or, in an
      // ordered list, a number — on a line the author never wrote. Fold it into the
      // item it belongs under instead.
      if (previous && entry.nestedLists.length > 0 && runsToText(entry.runs).length === 0) {
        previous.nestedLists.push(...entry.nestedLists);
        return kept;
      }

      kept.push(entry);
      return kept;
    }, []);

  if (entries.length === 0) return undefined;

  const items = entries.map(({ runs }) => runsToText(runs));
  const anyInline = entries.some(({ runs }) =>
    runs.some((run) => run.href || run.bold || run.italic),
  );
  const anyNested = entries.some(({ nestedLists }) => nestedLists.length > 0);

  return {
    type: 'list',
    items,
    ...(anyInline ? { itemsInline: entries.map(({ runs }) => runs) } : {}),
    ...(node.listType === 'number' ? { ordered: true } : {}),
    ...(anyNested ? { nestedLists: entries.map(({ nestedLists }) => nestedLists) } : {}),
  };
}

/**
 * Collapse runs to plain text, which stays the canonical value.
 *
 * Only the ends are trimmed — matching what the block mapper does. Interior
 * whitespace is passed through untouched: an earlier version also collapsed a
 * space before a newline, which silently rewrote 17 live articles that use
 * `"label: \n<text>"` inside one paragraph. Harmless in rendered HTML, but it made
 * the migration non-identity, and the whole point is that it is.
 */
function runsToText(runs: ArticleInline[]): string {
  return runs
    .map((run) => run.text)
    .join('')
    .trim();
}

/** Only emit `inline` when it carries something plain text cannot express. */
function withInline(runs: ArticleInline[]): { inline?: ArticleInline[] } {
  const meaningful = runs.some((run) => run.href || run.bold || run.italic);
  return meaningful ? { inline: runs } : {};
}

function headingLevel(tag: string | undefined): ArticleHeadingLevel | undefined {
  if (tag === 'h2') return 2;
  if (tag === 'h3') return 3;
  if (tag === 'h4') return 4;
  // h1 is the article title; h5/h6 are not part of the outline. Fall back to the
  // renderer's default (h2) rather than inventing a level.
  return undefined;
}

/**
 * Lexical's `headerState` names the AXIS a cell is a header *on*, which reads
 * backwards from the `scope` it should render with:
 *
 * - `ROW` (1)    = cell sits in the header ROW, so it labels the column beneath
 *                  it  -> `scope="col"`
 * - `COLUMN` (2) = cell sits in the header COLUMN, so it labels the row beside
 *                  it  -> `scope="row"`
 *
 * Authoritative for what Payload actually stores is its own markdown
 * transformer (`@payloadcms/richtext-lexical`,
 * `dist/features/experimental_table/markdownTransformer.js`): exporting writes
 * the `| --- |` divider after a row iff that row's cells are
 * `TableCellHeaderStates.ROW`, and importing sets `ROW` on every cell of the
 * header row.
 *
 * Reversing these silently swaps every `scope` AND — because the header row is
 * located by the same bit — drops the header entirely for the most common table
 * shape, one with a header row but no header column.
 */
const HEADER_STATE_ROW = 1;
const HEADER_STATE_COLUMN = 2;

function tableCell(node: LexicalNode, forceColumnHeader = false): ArticleTableCell {
  const runs = toInlineRuns(node.children);
  const text = runsToText(runs);
  const headerState = typeof node.headerState === 'number' ? node.headerState : 0;
  const isInHeaderRow = Boolean(headerState & HEADER_STATE_ROW);
  const isInHeaderColumn = Boolean(headerState & HEADER_STATE_COLUMN);
  const header = forceColumnHeader || isInHeaderRow || isInHeaderColumn;
  const scope = forceColumnHeader || isInHeaderRow ? 'col' : isInHeaderColumn ? 'row' : undefined;
  return {
    text,
    ...withInline(runs),
    ...(header ? { header: true } : {}),
    ...(scope ? { scope } : {}),
    ...(typeof node.colSpan === 'number' && node.colSpan > 1 ? { colSpan: node.colSpan } : {}),
    ...(typeof node.rowSpan === 'number' && node.rowSpan > 1 ? { rowSpan: node.rowSpan } : {}),
  };
}

function tableNodeToArticleBlock(node: LexicalNode): ArticleBlock | undefined {
  const rows = (node.children ?? [])
    .filter((row) => row.type === 'tablerow')
    .map((row) => (row.children ?? []).filter((cell) => cell.type === 'tablecell'));
  if (rows.every((row) => row.length === 0)) return undefined;

  // The header row is the row whose cells carry the header-ROW bit.
  const explicitHeaderIndex = rows.findIndex((row) =>
    row.some(
      (cell) =>
        typeof cell.headerState === 'number' && Boolean(cell.headerState & HEADER_STATE_ROW),
    ),
  );
  // A table can legitimately have only a header COLUMN (leftmost cells label
  // their rows) and no header row. Promoting row 0 there would delete a real
  // data row, so emit no header instead — `headerIndex === -1` makes
  // `rows[headerIndex]` undefined and leaves every row in the body.
  const hasHeaderColumn = rows.some((row) =>
    row.some(
      (cell) =>
        typeof cell.headerState === 'number' && Boolean(cell.headerState & HEADER_STATE_COLUMN),
    ),
  );
  const headerIndex = explicitHeaderIndex >= 0 ? explicitHeaderIndex : hasHeaderColumn ? -1 : 0;
  const headerCells = (rows[headerIndex] ?? []).map((cell) => tableCell(cell, true));
  const bodyRows = rows
    .filter((_, index) => index !== headerIndex)
    .map((row) => row.map((cell) => tableCell(cell)));

  return {
    type: 'table',
    columns: headerCells.map((cell) => cell.text),
    rows: bodyRows.map((row) => row.map((cell) => cell.text)),
    headerCells,
    bodyRows,
  };
}

/**
 * An embedded block node carries the same `fields` shape the blocks editor
 * stored, so it converts through the caller's existing block mapper — the
 * specialised components (table, chart, image, promoCard, …) keep one
 * implementation.
 */
export type EmbeddedBlockMapper = (fields: Record<string, unknown>) => ArticleBlock[];

export function lexicalToArticleBlocks(
  value: unknown,
  mapEmbeddedBlock: EmbeddedBlockMapper,
): ArticleBlock[] {
  if (!isLexicalRichText(value)) return [];
  const children = value.root?.children;
  if (!Array.isArray(children)) return [];

  return children.flatMap((node): ArticleBlock[] => {
    switch (node.type) {
      case 'heading': {
        const runs = toInlineRuns(node.children);
        const text = runsToText(runs);
        if (!text) return [];
        const level = headingLevel(node.tag);
        // Headings stay plain text: the quick-nav derives its anchors from this
        // string, and a link inside a heading would change the outline.
        return [{ type: 'heading', text, ...(level ? { level } : {}) }];
      }

      case 'paragraph': {
        const runs = toInlineRuns(node.children);
        const text = runsToText(runs);
        if (!text) return [];
        return [{ type: 'paragraph', text, ...withInline(runs) }];
      }

      case 'quote': {
        const runs = toInlineRuns(node.children);
        const text = runsToText(runs);
        return text ? [{ type: 'paragraph', text, ...withInline(runs) }] : [];
      }

      case 'list': {
        const block = listNodeToArticleBlock(node);
        return block ? [block] : [];
      }

      case 'upload': {
        if (node.relationTo !== 'media') return [];
        const media = resolvePayloadMedia(node.value, { requireImage: true });
        if (!media) return [];
        const caption = typeof node.fields?.caption === 'string' ? node.fields.caption.trim() : '';
        return [
          {
            type: 'image',
            src: media.url,
            ...(media.alt ? { alt: media.alt } : {}),
            ...(caption ? { caption } : {}),
            ...(media.width ? { width: media.width } : {}),
            ...(media.height ? { height: media.height } : {}),
          },
        ];
      }

      case 'table': {
        const table = tableNodeToArticleBlock(node);
        return table ? [table] : [];
      }

      case 'block': {
        // Specialised component embedded in the flow.
        const fields = node.fields;
        return fields ? mapEmbeddedBlock(fields) : [];
      }

      case 'horizontalrule':
        return [];

      default: {
        // Never silently drop copy: fall back to a paragraph if the unknown node
        // contains text.
        const runs = toInlineRuns(node.children);
        const text = runsToText(runs);
        return text ? [{ type: 'paragraph', text, ...withInline(runs) }] : [];
      }
    }
  });
}
